/**
 * CompassDisplay: HUD widget showing a compass that points toward the exit.
 *
 * Created in HudScene after the player picks up a compass item. Displays
 * a small compass dial at the bottom-right corner of the playable area
 * with a rotating needle that always points toward the maze exit.
 *
 * Features:
 *  - Smooth needle rotation toward the exit direction
 *  - Subtle oscillation wobble for a realistic feel
 *  - Periodic glint/fulgor effect with particle burst
 *  - Pop-in animation on show()
 *  - Magical disintegration animation on level completion
 */

import Phaser from 'phaser';
import { TEX_COMPASS_NEEDLE, TEX_GLOW } from '../utils/constants.js';
import {
  COMPASS_DISPLAY_SIZE,
  COMPASS_OSCILLATION_RANGE,
  COMPASS_OSCILLATION_SPEED,
  COMPASS_GLINT_INTERVAL_MIN,
  COMPASS_GLINT_INTERVAL_MAX,
  COMPASS_GLINT_DURATION,
  COMPASS_RING_COLOR,
  COMPASS_NEEDLE_COLOR,
  COMPASS_DEFAULT_ALPHA,
  COMPASS_PROXIMITY_ALPHA,
  COMPASS_PROXIMITY_RADIUS,
  COMPASS_EXIT_FULGOR_DISTANCE,
  COMPASS_EXIT_FULGOR_INTERVAL,
  TILE_SIZE,
} from '../config/game-config.js';

export default class CompassDisplay {
  /**
   * @param {Phaser.Scene} scene - The HUD scene.
   * @param {number} x - Center X position of the compass widget.
   * @param {number} y - Center Y position of the compass widget.
   */
  constructor(scene, x, y) {
    this.scene = scene;
    this.x = x;
    this.y = y;

    /** @type {boolean} Whether the compass is currently visible/active. */
    this._visible = false;

    /** @type {number} Target angle in radians (toward exit). */
    this._targetAngle = 0;

    /** @type {number} Current smoothed angle for the needle. */
    this._currentAngle = 0;

    /** @type {number} Oscillation phase accumulator (radians). */
    this._oscillationPhase = 0;

    /** @type {number} Exit grid column. */
    this._exitCol = 0;

    /** @type {number} Exit grid row. */
    this._exitRow = 0;

    /** @type {number} Current overall opacity applied to all elements. */
    this._containerAlpha = COMPASS_DEFAULT_ALPHA;

    /** @type {Phaser.Tweens.Tween|null} Proximity fade tween. */
    this._proximityTween = null;

    /** @type {boolean} Whether the exit-proximity fulgor effect is active. */
    this._fulgorActive = false;

    /** @type {Phaser.Time.TimerEvent|null} Repeating timer for fulgor bursts. */
    this._fulgorTimer = null;

    // --- Ring (outer circle) ---
    this._ring = scene.add.graphics();
    this._ring.setDepth(210);
    this._drawRing();

    // --- Cardinal labels ---
    const labelStyle = {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '7px',
      color: '#aaaaaa',
    };
    const r = COMPASS_DISPLAY_SIZE / 2 + 1;
    this._labelN = scene.add.text(x, y - r, 'N', labelStyle).setOrigin(0.5, 1).setDepth(211);
    this._labelS = scene.add.text(x, y + r, 'S', labelStyle).setOrigin(0.5, 0).setDepth(211);
    this._labelE = scene.add.text(x + r, y, 'E', labelStyle).setOrigin(0, 0.5).setDepth(211);
    this._labelW = scene.add.text(x - r, y, 'W', labelStyle).setOrigin(1, 0.5).setDepth(211);

    // --- Needle sprite ---
    this._needle = scene.add.sprite(x, y, TEX_COMPASS_NEEDLE);
    this._needle.setOrigin(0.5);
    this._needle.setDepth(212);
    this._needle.setScale(COMPASS_DISPLAY_SIZE / 32); // TEX_COMPASS_NEEDLE is 32×32

    // --- Center dot ---
    this._centerDot = scene.add.graphics();
    this._centerDot.setDepth(213);
    this._centerDot.fillStyle(0xffffff, 1);
    this._centerDot.fillCircle(x, y, 2);

    // --- Glow halo behind compass (soft, pulsing) ---
    this._glow = scene.add.graphics();
    this._glow.setDepth(209);
    this._glowAlpha = 0;

    // Start hidden
    this._setAllVisible(false);

    // Timers & tweens (created on show)
    /** @type {Phaser.Tweens.Tween|null} */
    this._glowTween = null;
    /** @type {Phaser.Tweens.Tween|null} */
    this._oscillationTween = null;
    /** @type {Phaser.Time.TimerEvent|null} */
    this._glintTimer = null;
    /** @type {Phaser.Time.TimerEvent|null} */
    this._updateTimer = null;
  }

  /**
   * Show the compass with a pop-in animation.
   * Called when the player collects a compass item.
   */
  show() {
    if (this._visible) return;
    this._visible = true;
    this._containerAlpha = COMPASS_DEFAULT_ALPHA;

    // Kill any leftover tweens from a previous disintegrate/hide cycle
    this._killAllElementTweens();
    this._resetPositions();
    this._setAllVisible(true);

    // Start from zero scale
    this._setAllScale(0);

    // Pop-in animation for labels
    const targets = this._getAnimTargets();
    this.scene.tweens.add({
      targets,
      scaleX: 1.3,
      scaleY: 1.3,
      duration: 300,
      ease: 'Back.easeOut',
      onComplete: () => {
        if (!this.scene) return;
        this.scene.tweens.add({
          targets,
          scaleX: 1,
          scaleY: 1,
          duration: 150,
          ease: 'Sine.easeInOut',
        });
      },
    });

    // Fade in ring and center dot (Graphics objects can't scale easily)
    const containerAlpha = this._containerAlpha;
    if (this._ring) {
      this.scene.tweens.add({
        targets: this._ring,
        alpha: { from: 0, to: containerAlpha },
        duration: 300,
        ease: 'Sine.easeOut',
      });
    }
    if (this._centerDot) {
      this.scene.tweens.add({
        targets: this._centerDot,
        alpha: { from: 0, to: containerAlpha },
        duration: 300,
        ease: 'Sine.easeOut',
      });
    }

    // Needle gets its own scale (ratio to 32px texture)
    const needleScale = COMPASS_DISPLAY_SIZE / 32;
    this._needle.setScale(0);
    this.scene.tweens.add({
      targets: this._needle,
      scaleX: needleScale * 1.3,
      scaleY: needleScale * 1.3,
      duration: 300,
      ease: 'Back.easeOut',
      onComplete: () => {
        if (!this.scene) return;
        this.scene.tweens.add({
          targets: this._needle,
          scaleX: needleScale,
          scaleY: needleScale,
          duration: 150,
          ease: 'Sine.easeInOut',
        });
      },
    });

    // Start pulsing glow
    this._startGlow();

    // Start oscillation cycle
    this._startOscillation();

    // Start periodic glint
    this._scheduleGlint();

    // Start per-frame angle update via a fast timer (60fps equivalent)
    this._updateTimer = this.scene.time.addEvent({
      delay: 16,
      callback: this._updateNeedleAngle,
      callbackScope: this,
      loop: true,
    });
  }

  /**
   * Update the target direction based on the player and exit positions.
   *
   * @param {number} playerCol - Player grid column (expanded grid).
   * @param {number} playerRow - Player grid row (expanded grid).
   * @param {number} exitCol - Exit grid column.
   * @param {number} exitRow - Exit grid row.
   */
  updateDirection(playerCol, playerRow, exitCol, exitRow) {
    this._exitCol = exitCol;
    this._exitRow = exitRow;

    // Calculate center pixel positions
    const px = playerCol * TILE_SIZE + TILE_SIZE / 2;
    const py = playerRow * TILE_SIZE + TILE_SIZE / 2;
    const ex = exitCol * TILE_SIZE + TILE_SIZE / 2;
    const ey = exitRow * TILE_SIZE + TILE_SIZE / 2;

    // atan2 gives angle from player to exit. In Phaser, rotation=0 is right.
    // We add PI/2 so that 0 means "up" (north) for the needle.
    this._targetAngle = Math.atan2(ey - py, ex - px) + Math.PI / 2;

    // Check distance to exit for fulgor effect (Manhattan on logical cells).
    // Grid positions are in expanded-grid coords (odd = cell), so convert.
    const playerLogicalCol = (playerCol - 1) / 2;
    const playerLogicalRow = (playerRow - 1) / 2;
    const exitLogicalCol = (exitCol - 1) / 2;
    const exitLogicalRow = (exitRow - 1) / 2;
    const manhattan = Math.abs(playerLogicalCol - exitLogicalCol)
      + Math.abs(playerLogicalRow - exitLogicalRow);

    if (manhattan <= COMPASS_EXIT_FULGOR_DISTANCE && !this._fulgorActive) {
      this._startFulgor();
    } else if (manhattan > COMPASS_EXIT_FULGOR_DISTANCE && this._fulgorActive) {
      this._stopFulgor();
    }
  }

  /**
   * Update the compass opacity based on the player's screen proximity.
   * When the player is near the compass widget on screen, the compass
   * fades to a lower alpha so the maze behind it remains visible.
   * Otherwise the compass stays at its default, more opaque state.
   *
   * @param {number} playerScreenX - Player X position in screen/HUD coordinates.
   * @param {number} playerScreenY - Player Y position in screen/HUD coordinates.
   */
  updateProximity(playerScreenX, playerScreenY) {
    if (!this._visible || !this.scene) return;

    const dx = playerScreenX - this.x;
    const dy = playerScreenY - this.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    // Determine target alpha based on distance
    let targetAlpha;
    if (dist <= COMPASS_PROXIMITY_RADIUS) {
      // Interpolate: closer = more transparent
      const t = dist / COMPASS_PROXIMITY_RADIUS; // 0 = on top, 1 = at edge
      targetAlpha = COMPASS_PROXIMITY_ALPHA + t * (COMPASS_DEFAULT_ALPHA - COMPASS_PROXIMITY_ALPHA);
    } else {
      targetAlpha = COMPASS_DEFAULT_ALPHA;
    }

    // Only tween if there's a meaningful change
    if (Math.abs(this._containerAlpha - targetAlpha) < 0.02) return;

    // Cancel any running proximity tween
    if (this._proximityTween) {
      this._proximityTween.stop();
      this._proximityTween = null;
    }

    this._proximityTween = this.scene.tweens.add({
      targets: this,
      _containerAlpha: targetAlpha,
      duration: 200,
      ease: 'Sine.easeInOut',
      onUpdate: () => {
        this._applyContainerAlpha();
      },
    });
  }

  /**
   * Play the magical disintegration animation.
   * The compass fragments scatter outward and fade away.
   *
   * @param {Function} [onComplete] - Optional callback after animation completes.
   */
  disintegrate(onComplete) {
    if (!this._visible || !this.scene) {
      if (onComplete) onComplete();
      return;
    }

    // Stop all ongoing effects
    this._stopGlow();
    this._stopOscillation();
    this._stopGlint();
    this._stopFulgor();
    this._stopProximityTween();
    if (this._updateTimer) {
      this._updateTimer.remove(false);
      this._updateTimer = null;
    }

    // --- Particle burst from compass center ---
    try {
      const emitter = this.scene.add.particles(this.x, this.y, TEX_GLOW, {
        lifespan: { min: 400, max: 900 },
        speed: { min: 30, max: 90 },
        scale: { start: 0.5, end: 0 },
        alpha: { start: 0.9, end: 0 },
        angle: { min: 0, max: 360 },
        frequency: -1,
      });
      emitter.setDepth(220);
      emitter.explode(16);

      this.scene.time.delayedCall(1000, () => {
        if (emitter) emitter.destroy();
      });
    } catch (_) { /* graceful fallback */ }

    // --- Scatter all visual elements outward + fade ---
    const elements = [this._needle, this._labelN, this._labelS, this._labelE, this._labelW];
    const angles = [
      Math.random() * Math.PI * 2,
      -Math.PI / 2,    // N goes up
      Math.PI / 2,     // S goes down
      0,               // E goes right
      Math.PI,         // W goes left
    ];

    for (let i = 0; i < elements.length; i++) {
      const el = elements[i];
      if (!el || !el.active) continue;
      const angle = angles[i];
      const dist = Phaser.Math.Between(25, 50);
      this.scene.tweens.add({
        targets: el,
        x: el.x + Math.cos(angle) * dist,
        y: el.y + Math.sin(angle) * dist,
        alpha: 0,
        scaleX: 0,
        scaleY: 0,
        duration: Phaser.Math.Between(400, 700),
        ease: 'Quad.easeIn',
        onComplete: () => {
          if (el && el.active) el.setVisible(false);
        },
      });
    }

    // Fade ring and glow graphics
    this.scene.tweens.add({
      targets: { val: 1 },
      val: 0,
      duration: 500,
      ease: 'Quad.easeIn',
      onUpdate: (_tween, target) => {
        if (this._ring) {
          this._ring.setAlpha(target.val);
        }
        if (this._centerDot) {
          this._centerDot.setAlpha(target.val);
        }
      },
      onComplete: () => {
        this._visible = false;
        this._setAllVisible(false);
        if (onComplete) onComplete();
      },
    });
  }

  /**
   * Instantly hide the compass (no animation). Used on level change.
   */
  hide() {
    this._visible = false;
    this._stopGlow();
    this._stopOscillation();
    this._stopGlint();
    this._stopFulgor();
    this._stopProximityTween();
    this._killAllElementTweens();
    if (this._updateTimer) {
      this._updateTimer.remove(false);
      this._updateTimer = null;
    }
    this._containerAlpha = COMPASS_DEFAULT_ALPHA;
    this._setAllVisible(false);
    this._resetPositions();
  }

  /**
   * Destroy all display elements and clean up resources.
   */
  destroy() {
    this._stopGlow();
    this._stopOscillation();
    this._stopGlint();
    this._stopFulgor();
    this._stopProximityTween();
    if (this._updateTimer) {
      this._updateTimer.remove(false);
      this._updateTimer = null;
    }

    if (this._ring) { this._ring.destroy(); this._ring = null; }
    if (this._needle) { this._needle.destroy(); this._needle = null; }
    if (this._centerDot) { this._centerDot.destroy(); this._centerDot = null; }
    if (this._glow) { this._glow.destroy(); this._glow = null; }
    if (this._labelN) { this._labelN.destroy(); this._labelN = null; }
    if (this._labelS) { this._labelS.destroy(); this._labelS = null; }
    if (this._labelE) { this._labelE.destroy(); this._labelE = null; }
    if (this._labelW) { this._labelW.destroy(); this._labelW = null; }
  }

  // ---------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------

  /**
   * Apply the current container alpha to all visible elements.
   * This is used for the proximity fade effect — the compass becomes
   * more translucent when the player is near it on screen.
   * @private
   */
  _applyContainerAlpha() {
    const a = this._containerAlpha;
    if (this._ring) this._ring.setAlpha(a);
    if (this._needle) this._needle.setAlpha(a);
    if (this._centerDot) this._centerDot.setAlpha(a);
    if (this._labelN) this._labelN.setAlpha(a);
    if (this._labelS) this._labelS.setAlpha(a);
    if (this._labelE) this._labelE.setAlpha(a);
    if (this._labelW) this._labelW.setAlpha(a);
  }

  /**
   * Draw the compass ring (outer circle + tick marks).
   * @private
   */
  _drawRing() {
    if (!this._ring) return;
    this._ring.clear();
    const r = COMPASS_DISPLAY_SIZE / 2;

    // Outer ring (full alpha — container alpha controls overall visibility)
    this._ring.lineStyle(1.5, COMPASS_RING_COLOR, 1);
    this._ring.strokeCircle(this.x, this.y, r);

    // Inner decorative ring
    this._ring.lineStyle(0.5, COMPASS_RING_COLOR, 0.5);
    this._ring.strokeCircle(this.x, this.y, r - 3);

    // Tick marks at N, E, S, W
    const tickLen = 3;
    const dirs = [
      { angle: -Math.PI / 2, len: tickLen },     // N
      { angle: 0, len: tickLen - 1 },             // E
      { angle: Math.PI / 2, len: tickLen },        // S
      { angle: Math.PI, len: tickLen - 1 },        // W
    ];

    this._ring.lineStyle(1, COMPASS_RING_COLOR, 0.85);
    for (const d of dirs) {
      const cos = Math.cos(d.angle);
      const sin = Math.sin(d.angle);
      const x1 = this.x + cos * (r - 1);
      const y1 = this.y + sin * (r - 1);
      const x2 = this.x + cos * (r - 1 - d.len);
      const y2 = this.y + sin * (r - 1 - d.len);
      this._ring.lineBetween(x1, y1, x2, y2);
    }
  }

  /**
   * Draw the glow halo behind the compass.
   * @private
   */
  _drawGlow() {
    if (!this._glow) return;
    this._glow.clear();
    if (this._glowAlpha <= 0) return;

    this._glow.fillStyle(0xffffff, this._glowAlpha * 0.3);
    this._glow.fillCircle(this.x, this.y, COMPASS_DISPLAY_SIZE / 2 + 6);
    this._glow.fillStyle(0xffffff, this._glowAlpha * 0.6);
    this._glow.fillCircle(this.x, this.y, COMPASS_DISPLAY_SIZE / 2 + 2);
  }

  /**
   * Start the pulsing glow effect behind the compass.
   * @private
   */
  _startGlow() {
    this._glowAlpha = 0.15;
    this._drawGlow();

    this._glowTween = this.scene.tweens.add({
      targets: this,
      _glowAlpha: { from: 0.15, to: 0.04 },
      duration: 2000,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
      onUpdate: () => {
        this._drawGlow();
      },
    });
  }

  /**
   * Stop the glow effect.
   * @private
   */
  _stopGlow() {
    if (this._glowTween) {
      this._glowTween.stop();
      this._glowTween = null;
    }
    this._glowAlpha = 0;
    if (this._glow) {
      this._glow.clear();
    }
  }

  /**
   * Start the oscillation wobble on the needle.
   * Uses a continuously updating tween on a phase accumulator.
   * @private
   */
  _startOscillation() {
    this._oscillationPhase = 0;
    this._oscillationTween = this.scene.tweens.add({
      targets: this,
      _oscillationPhase: Math.PI * 2,
      duration: COMPASS_OSCILLATION_SPEED,
      repeat: -1,
    });
  }

  /**
   * Stop the oscillation.
   * @private
   */
  _stopOscillation() {
    if (this._oscillationTween) {
      this._oscillationTween.stop();
      this._oscillationTween = null;
    }
    this._oscillationPhase = 0;
  }

  /**
   * Stop the proximity fade tween.
   * @private
   */
  _stopProximityTween() {
    if (this._proximityTween) {
      this._proximityTween.stop();
      this._proximityTween = null;
    }
  }

  /**
   * Kill ALL active tweens targeting the compass visual elements.
   * This prevents stale disintegrate/pop-in tweens from overwriting
   * positions and alpha values after a hide() → show() cycle.
   * @private
   */
  _killAllElementTweens() {
    if (!this.scene || !this.scene.tweens) return;
    const targets = [
      this._needle, this._labelN, this._labelS,
      this._labelE, this._labelW, this._ring, this._centerDot,
    ];
    for (const t of targets) {
      if (t) this.scene.tweens.killTweensOf(t);
    }
  }

  /**
   * Start the exit-proximity fulgor effect.
   * Emits repeated bright bursts from the compass to signal the exit is near.
   * @private
   */
  _startFulgor() {
    if (this._fulgorActive) return;
    this._fulgorActive = true;

    // Emit one burst immediately
    this._emitFulgor();

    // Then repeat at interval
    this._fulgorTimer = this.scene.time.addEvent({
      delay: COMPASS_EXIT_FULGOR_INTERVAL,
      callback: this._emitFulgor,
      callbackScope: this,
      loop: true,
    });
  }

  /**
   * Stop the exit-proximity fulgor effect.
   * @private
   */
  _stopFulgor() {
    this._fulgorActive = false;
    if (this._fulgorTimer) {
      this._fulgorTimer.remove(false);
      this._fulgorTimer = null;
    }
  }

  /**
   * Emit a single fulgor burst — a bright flash of the compass ring
   * plus a radial particle spray.
   * @private
   */
  _emitFulgor() {
    if (!this.scene || !this._visible) return;

    // --- Bright ring flash ---
    if (this._ring && this._ring.active) {
      const origAlpha = this._containerAlpha;
      this.scene.tweens.add({
        targets: this._ring,
        alpha: { from: 1, to: origAlpha },
        duration: 300,
        ease: 'Quad.easeOut',
      });
    }

    // --- Needle bright pulse ---
    if (this._needle && this._needle.active) {
      this._needle.setTint(0xffffff);
      this.scene.tweens.add({
        targets: this._needle,
        alpha: { from: 1, to: this._containerAlpha },
        duration: 300,
        ease: 'Quad.easeOut',
        onComplete: () => {
          if (this._needle && this._needle.active) {
            this._needle.clearTint();
          }
        },
      });
    }

    // --- Particle radial spray ---
    try {
      const emitter = this.scene.add.particles(this.x, this.y, TEX_GLOW, {
        lifespan: { min: 250, max: 500 },
        speed: { min: 15, max: 50 },
        scale: { start: 0.4, end: 0 },
        alpha: { start: 0.8, end: 0 },
        angle: { min: 0, max: 360 },
        frequency: -1,
      });
      emitter.setDepth(216);
      emitter.explode(Phaser.Math.Between(4, 7));

      this.scene.time.delayedCall(600, () => {
        if (emitter) emitter.destroy();
      });
    } catch (_) { /* graceful fallback */ }
  }

  /**
   * Per-frame update for the needle angle.
   * Smoothly interpolates toward the target angle with oscillation.
   * @private
   */
  _updateNeedleAngle() {
    if (!this._needle || !this._visible) return;

    // Smooth rotation toward target angle
    let diff = this._targetAngle - this._currentAngle;
    // Normalize to [-PI, PI]
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;

    // Lerp factor (smooth but responsive)
    this._currentAngle += diff * 0.08;

    // Add oscillation wobble
    const wobble = Math.sin(this._oscillationPhase) * COMPASS_OSCILLATION_RANGE;

    this._needle.setRotation(this._currentAngle + wobble);
  }

  /**
   * Schedule the next random glint.
   * @private
   */
  _scheduleGlint() {
    if (!this.scene || !this._visible) return;
    const delay = Phaser.Math.Between(COMPASS_GLINT_INTERVAL_MIN, COMPASS_GLINT_INTERVAL_MAX);
    this._glintTimer = this.scene.time.delayedCall(delay, () => {
      this._playGlint();
    });
  }

  /**
   * Play a glint flash effect on the compass.
   * @private
   */
  _playGlint() {
    if (!this.scene || !this._visible) return;

    // Flash needle brightness
    if (this._needle && this._needle.active) {
      this._needle.setTint(0xffffff);
      const baseAlpha = this._containerAlpha;
      this.scene.tweens.add({
        targets: this._needle,
        alpha: { from: baseAlpha, to: baseAlpha * 0.4 },
        duration: COMPASS_GLINT_DURATION / 2,
        yoyo: true,
        ease: 'Sine.easeInOut',
        onComplete: () => {
          if (this._needle && this._needle.active) {
            this._needle.clearTint();
            this._needle.setAlpha(this._containerAlpha);
          }
        },
      });
    }

    // Particle burst
    try {
      const emitter = this.scene.add.particles(this.x, this.y, TEX_GLOW, {
        lifespan: 350,
        speed: { min: 10, max: 30 },
        scale: { start: 0.3, end: 0 },
        alpha: { start: 0.7, end: 0 },
        frequency: -1,
      });
      emitter.setDepth(215);
      emitter.explode(3);
      this.scene.time.delayedCall(400, () => {
        if (emitter) emitter.destroy();
      });
    } catch (_) { /* graceful fallback */ }

    this._scheduleGlint();
  }

  /**
   * Stop glint timer.
   * @private
   */
  _stopGlint() {
    if (this._glintTimer) {
      this._glintTimer.remove(false);
      this._glintTimer = null;
    }
  }

  /**
   * Set visibility on all child elements.
   * @private
   * @param {boolean} visible
   */
  _setAllVisible(visible) {
    if (this._ring) this._ring.setVisible(visible);
    if (this._needle) this._needle.setVisible(visible);
    if (this._centerDot) this._centerDot.setVisible(visible);
    if (this._glow) this._glow.setVisible(visible);
    if (this._labelN) this._labelN.setVisible(visible);
    if (this._labelS) this._labelS.setVisible(visible);
    if (this._labelE) this._labelE.setVisible(visible);
    if (this._labelW) this._labelW.setVisible(visible);

    // Reset alpha when making visible (use container alpha for default opacity)
    if (visible) {
      const a = this._containerAlpha;
      if (this._ring) this._ring.setAlpha(a);
      if (this._needle) this._needle.setAlpha(a);
      if (this._centerDot) this._centerDot.setAlpha(a);
      if (this._labelN) this._labelN.setAlpha(a);
      if (this._labelS) this._labelS.setAlpha(a);
      if (this._labelE) this._labelE.setAlpha(a);
      if (this._labelW) this._labelW.setAlpha(a);
    }
  }

  /**
   * Set scale on animated child elements (not needle — it has its own scale).
   * @private
   * @param {number} s
   */
  _setAllScale(s) {
    if (this._labelN) this._labelN.setScale(s);
    if (this._labelS) this._labelS.setScale(s);
    if (this._labelE) this._labelE.setScale(s);
    if (this._labelW) this._labelW.setScale(s);
    // Ring and centerDot are graphics — scale via alpha for simplicity
    if (s === 0) {
      if (this._ring) this._ring.setAlpha(0);
      if (this._centerDot) this._centerDot.setAlpha(0);
    }
  }

  /**
   * Get array of animatable child targets (for pop-in/out tweens).
   * @private
   * @returns {object[]}
   */
  _getAnimTargets() {
    const t = [];
    if (this._labelN) t.push(this._labelN);
    if (this._labelS) t.push(this._labelS);
    if (this._labelE) t.push(this._labelE);
    if (this._labelW) t.push(this._labelW);
    return t;
  }

  /**
   * Reset positions of all elements to their original locations.
   * Used after disintegration to prepare for next show().
   * @private
   */
  _resetPositions() {
    const r = COMPASS_DISPLAY_SIZE / 2 + 1;

    if (this._labelN) {
      this._labelN.setPosition(this.x, this.y - r);
      this._labelN.setScale(1);
    }
    if (this._labelS) {
      this._labelS.setPosition(this.x, this.y + r);
      this._labelS.setScale(1);
    }
    if (this._labelE) {
      this._labelE.setPosition(this.x + r, this.y);
      this._labelE.setScale(1);
    }
    if (this._labelW) {
      this._labelW.setPosition(this.x - r, this.y);
      this._labelW.setScale(1);
    }
    if (this._needle) {
      this._needle.setPosition(this.x, this.y);
      this._needle.setScale(COMPASS_DISPLAY_SIZE / 32);
      this._needle.setRotation(0);
      this._needle.setAlpha(this._containerAlpha);
    }
    if (this._ring) {
      this._ring.setAlpha(this._containerAlpha);
      this._drawRing();
    }
    if (this._centerDot) {
      this._centerDot.setAlpha(this._containerAlpha);
    }

    this._currentAngle = 0;
    this._targetAngle = 0;
    this._oscillationPhase = 0;
  }
}
